import { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UserAvatar from "../../components/UserAvatar";
import { FeaturedCourseSkeleton } from "../../components/FeaturedCourseCard";

function EnrolledCourseCard({ row }) {
  const { t } = useTranslation();
  const [coverFailed, setCoverFailed] = useState(false);
  const course = row.course;
  const isRecorded = course.course_type === "recorded";
  const href = isRecorded
    ? `/lesson/${course.slug}/${course?.trial_lessons?.id}`
    : `/room/${course.slug}/${row.room_no}`;

  return (
    <article className="fc-card dh-course">
      <Link to={href} className="fc-cover" tabIndex={-1} aria-hidden="true">
        {course.cover_image && !coverFailed ? (
          <img src={course.cover_image} alt="" loading="lazy" onError={() => setCoverFailed(true)} />
        ) : (
          <div className="fc-cover-fallback"><i className="fa-light fa-book-open" /></div>
        )}
        {isRecorded ? (
          <span className="fc-type"><i className="fa-solid fa-circle-play" /> {t("dashHome.recorded")}</span>
        ) : (
          <span className="fc-type fc-type--live"><span className="fc-live-dot" /> {t("dashHome.live")}</span>
        )}
      </Link>
      <div className="fc-body">
        <h3 className="fc-title"><Link to={href}>{course.title}</Link></h3>
        <div className="fc-teacher">
          <UserAvatar src={course?.teacher?.avatar} name={course?.teacher?.name} size={32} className="fc-avatar" />
          <span className="fc-teacher-name">{course?.teacher?.name}</span>
        </div>
        <div className="dh-course-foot">
          <span className="fc-meta-item">
            <i className="fa-light fa-book-open" /> {t("dashHome.lessons_count", { count: Number(course.lessons_count) || 0 })}
          </span>
          <Link to={href} className={`dh-course-btn${isRecorded ? "" : " dh-course-btn--live"}`}>
            {isRecorded ? t("dashHome.open") : t("dashHome.join")}
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function StudentHome() {
  const { t } = useTranslation();
  const select_course = useSelector((state) => state.enrollmentReducer);
  // the slice starts with id-only placeholders; only rows with a course are real enrollments
  const rows = (select_course?.courses || []).filter((row) => row?.course?.slug);
  const loading = select_course?.loading || (rows.length === 0 && select_course?.courses?.some((row) => !row?.course));
  const liveCount = rows.filter((row) => row.course.course_type !== "recorded").length;

  const stats = [
    { icon: "fa-book-open-reader", label: t("dashHome.enrolled_courses"), value: rows.length, tone: 1 },
    { icon: "fa-signal-stream", label: t("dashHome.live_courses"), value: liveCount, tone: 2 },
    { icon: "fa-circle-play", label: t("dashHome.recorded_courses"), value: rows.length - liveCount, tone: 3 },
  ];

  return (
    <div className="dh">
      <div className="row g-3">
        {stats.map((stat) => (
          <div className="col-md-4" key={stat.label}>
            <div className="dh-stat">
              <span className={`dh-stat-icon dh-tone-${stat.tone}`}><i className={`fa-light ${stat.icon}`} /></span>
              <div>
                <span className="dh-stat-value">{loading ? "–" : stat.value}</span>
                <span className="dh-stat-label">{stat.label}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="dh-section-head">
        <h2>{t("dashHome.your_courses")}</h2>
        <Link to="/home/my-lessons" className="dh-link">
          {t("dashHome.view_lessons")} <i className="fa-light fa-arrow-right" />
        </Link>
      </div>

      {loading ? (
        <div className="row g-3">
          {[0, 1, 2].map((i) => (
            <div className="col-xl-4 col-md-6" key={i}><FeaturedCourseSkeleton /></div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="dh-empty">
          <span className="dh-empty-icon dh-tone-3"><i className="fa-light fa-graduation-cap" /></span>
          <h3>{t("dashHome.empty_student_title")}</h3>
          <p>{t("dashHome.empty_student_desc")}</p>
          <Link to="/all-courses" className="dh-btn">{t("dashHome.browse_courses")}</Link>
        </div>
      ) : (
        <div className="row g-3">
          {rows.map((row, j) => (
            <div className="col-xl-4 col-md-6" key={row.id ?? j}>
              <EnrolledCourseCard row={row} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
